import React, { useState, useEffect, useRef, useEffectEvent } from "react";
import axios from "axios";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import { Link } from "react-router-dom";
// Import ikon
import { Heart, Clock, Users, Handshake, ChevronLeft, ChevronRight } from "lucide-react";
import { EditableText } from "./editabletext";
// Import logo

// --- DANE DOMYŚLNE ---
const defaultSlides = [
  {
    link: "https://images.unsplash.com/photo-1515703407324-5f75323e2528?auto=format&fit=crop&q=80&w=1920",
    title: "PTH Koziołki",
    desc: "Duma Poznania. Walczymy do końca."
  },
  {
    link: "https://images.unsplash.com/photo-1580748141549-71748dbe0bdc?auto=format&fit=crop&q=80&w=1920",
    title: "Sekcja Juniorów",
    desc: "Dołącz do najlepszej akademii hokejowej w Wielkopolsce."
  },
  {
    link: "https://images.unsplash.com/photo-1552065874-9b65345970c3?auto=format&fit=crop&q=80&w=1920",
    title: "Sezon 2025/26",
    desc: "Wspieraj nas w drodze po mistrzostwo."
  }
];

const eventFallbacks = [
  "https://images.unsplash.com/photo-1580748141549-71748dbe0bdc?auto=format&fit=crop&q=80&w=600",
  "https://images.unsplash.com/photo-1545471977-94cac22e71ed?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  "https://plus.unsplash.com/premium_photo-1661964423428-ac2381ffcef2?q=80&w=2906&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
];

const sponsors = ["CCM", "BAUER", "WARRIOR", "MIASTO POZNAŃ", "CHWIAŁKA", "INTERSPORT", "4F", "CANAL+"];

// --- STYLED COMPONENTS (PREFIX: Home) ---

// Animacje
const HomeFadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HomeZoom = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.1); }
`;

const HomeMarqueeAnim = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const pulseAnim = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

// Globalne style
const HomeGlobalStyle = createGlobalStyle`
  body {
    background-color: #ffffff;
    color: #111;
    margin: 0;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    overflow-x: hidden;
  }
  
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const HomeContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
`;

// Ekran ładowania
const HomeLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  img {
    height: 80px;
    margin-bottom: 20px;
    animation: ${pulseAnim} 1.5s infinite ease-in-out;
  }

  span {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 1.2rem;
    color: #111;
  }
`;

// --- HERO SLIDER ---
const HomeHeroSlider = styled.section`
  height: 85vh; 
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: #000;
`;

const HomeSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => (props.active ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  z-index: ${props => (props.active ? 2 : 1)};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 60%);
  }
`;

const HomeSlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: ${props => props.active ? css`${HomeZoom} 12s linear infinite` : 'none'};
`;

const HomeSlideContent = styled.div`
  position: absolute;
  bottom: 80px;
  left: 5%;
  z-index: 3;
  max-width: 900px;
  color: #fff;
`;

const HomeSlideTitle = styled.h2`
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 800;
  text-transform: uppercase;
  line-height: 0.9;
  margin: 0 0 20px 0;
  letter-spacing: -2px;
  opacity: 0;
  animation: ${props => props.active ? css`${HomeFadeUp} 0.8s ease-out 0.3s forwards` : 'none'};
`;

const HomeSlideDesc = styled.p`
  font-size: 1.1rem;
  font-weight: 400;
  margin-bottom: 30px;
  border-left: 2px solid #fff;
  padding-left: 20px;
  max-width: 500px;
  opacity: 0;
  animation: ${props => props.active ? css`${HomeFadeUp} 0.8s ease-out 0.5s forwards` : 'none'};
`;

const HomeSliderControls = styled.div`
  position: absolute;
  bottom: 40px;
  right: 5%;
  z-index: 10;
  display: flex;
  gap: 15px;
`;

const HomeDot = styled.button`
  width: 60px;
  height: 4px;
  border: none;
  background: ${props => props.active ? '#fff' : 'rgba(255,255,255,0.3)'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #fff;
  }
`;

// --- EVENTS CAROUSEL ---
const HomeEventsSection = styled.section`
  padding: 80px 0 80px 0;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const HomeEventsHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 5% 30px 5%;
`;
const HomeSectionLabel = styled.span`
  
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #999;
  display: block;
`;

const HomeEventsRelativeWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const HomeNavBtn = styled.button`
  width: 50px;
  height: 50px;
  border: 1px solid #eee;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  color: #000;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  
  /* Nowość: Ukrywanie przycisku w zależności od propsa (czy jest overflow) */
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  opacity: ${props => props.show ? 1 : 0};

  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}

  &:hover {
    background: #000;
    color: #fff;
    border-color: #000;
  }

  @media (max-width: 1024px) {
    display: none; /* Na tabletach i smartfonach polegamy na dotyku */
  }
`;

const HomeEventsWrapper = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  /* Centrowanie na mobile - ustawiamy stały odstęp boczny */
  padding: 20px 5% 40px 5%; 
  
  /* Ukrycie paska przewijania */
  -ms-overflow-style: none;  
  scrollbar-width: none;  
  &::-webkit-scrollbar {
    display: none;
  }

  /* Na mniejszych ekranach wycentrowana karta i ucięta kolejna */
  @media (max-width: 768px) {
    padding: 20px 5vw 40px 5vw;
    scroll-padding-left: 5vw;
  }
`;

const HomeEventCard = styled.div`
  min-width: 320px;
  max-width: 350px;
  flex: 0 0 auto; /* Nie pozwalamy karcie się kurczyć */
  height: 450px;
  border: 1px solid #eee;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  scroll-snap-align: start; /* Przyciąga lewą krawędź do lewej osi ekranu (uwzględniając padding) */
  position: relative;
  overflow: hidden;

  /* Responsywność karty dla "uciętego" efektu */
  @media (max-width: 768px) {
    /* 85vw szerokości sprawia, że przy paddingach na 5vw karta 
       zostawia 5vw po prawej na pokazanie "kawałka" następnej karty */
    min-width: 80vw; 
    max-width: 80vw;
    height: 400px;
  }

`;
const HomeEventImageWrapper = styled.div`
  width: 100%;
  height: 60%;
  overflow: hidden;
  position: relative;
`;

const HomeEventImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.5s ease;
`;

const HomeEventContent = styled.div`
  height: 40%;
  padding: 25px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fff;
  transition: background 0.4s;
`;

const HomeEventDate = styled.div`
  display: flex;
  flex-direction: column;
  
  span.day {
    font-size: 2.5rem;
    font-weight: 900;
    line-height: 1;
    color: #000;
    transition: color 0.4s;
  }
  
  span.month {
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #999;
    letter-spacing: 2px;
    transition: color 0.4s;
  }
`;

const HomeEventInfo = styled.div`
  margin-top: auto;
`;

const HomeEventTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 5px 0;
  color: #000;
  text-transform: uppercase;
  transition: color 0.4s;
`;

const HomeEventMeta = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  transition: color 0.4s;
`;

const HomeNoEvents = styled.div`
  padding: 40px;
  border: 1px dashed #ccc;
  color: #999;
  font-weight: bold;
  text-transform: uppercase;
  min-width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

// --- ABOUT SECTION ---
const HomeAboutSection = styled.section`
  padding: 120px 5%;
  background-color: #fff;
  color: #111;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 80px 5%;
  }
`;

const HomeAboutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: flex-start; 
  text-align: left;
`;

const HomeAboutTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-transform: uppercase;
  font-weight: 900;
  margin: 0;
  line-height: 1;
  letter-spacing: -2px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap; 

  span {
    color: #888;
    display: block;
  }
`;

const HomeLogoImg = styled.img`
  height: 80px;
  width: auto;
  object-fit: contain;
  
  @media (max-width: 768px) {
      height: 60px;
  }
`;

const HomeAboutText = styled.p`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.7;
  max-width: 550px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HomeAboutStats = styled.div`
  display: flex;
  gap: 60px;
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 30px;
  width: 100%;
  flex-wrap: wrap; 

  @media (max-width: 768px) {
    gap: 30px;
    justify-content: flex-start;
  }
`;

const HomeStatItem = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 3rem;
    font-weight: 800;
    color: #000;
    line-height: 1;
    
    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }
  
  span {
    font-size: 0.8rem;
    text-transform: uppercase;
    color: #999;
    letter-spacing: 1px;
    margin-top: 5px;
  }
`;

const HomeValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const HomeValueCard = styled.div`
  background: #f9f9f9;
  padding: 40px 30px;
  border: 1px solid #eee;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  display: flex;
  flex-direction: column;
  gap: 15px;



  h4 {
    font-size: 1.1rem;
    text-transform: uppercase;
    margin: 10px 0 0 0;
    font-weight: 800;
  }

  p {
    font-size: 0.9rem;
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
`;

const HomeIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  margin-bottom: 10px;
  color: #ccc;
  transition: all 0.3s;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;


// --- NEXT MATCH SECTION ---
const HomeMatchSection = styled.section`
  padding: 100px 5%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const HomeVersusCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1100px;
  padding: 80px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 40px;
    text-align: center;
    padding: 40px 0;
  }
`;
const HomeTeamCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: ${props => props.align === 'right' ? 'flex-end' : 'flex-start'};
  text-align: ${props => props.align || 'center'};
  min-width: 0;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const HomeTeamName = styled.h3`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  margin: 0;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -2px;
  line-height: 1;
  text-align: inherit;
  
  /* Pozwala flexboxowi skurczyć element poniżej długości słowa */
  min-width: 0; 
  overflow-wrap: break-word; 
  word-break: break-word;

  /* EFEKT GRADIENTU DLA KOLORÓW ROCZNIKA */
  background: ${props => (props.color1 && props.color2) ? `linear-gradient(90deg, ${props.color1}, ${props.color2})` : 'transparent'};
  -webkit-background-clip: ${props => (props.color1 && props.color2) ? 'text' : 'border-box'};
  -webkit-text-fill-color: ${props => (props.color1 && props.color2) ? 'transparent' : 'inherit'};
  color: ${props => props.color || '#111'};
`;

const HomeCoachLabel = styled.div`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #999;
  margin-top: 15px;
  font-weight: 700;
  
  span {
    color: #111;
  }
`;

const HomeMatchInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  flex: 0 0 auto;
`;

const HomeMatchVs = styled.div`
  font-size: 2rem;
  font-weight: 900;
  font-style: italic;
  margin-bottom: 10px;
  color: #ddd;
`;

const HomeMatchDate = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HomeMatchLocation = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: 5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
`;

const HomeCTAButton = styled(Link)`
  margin-top: 40px;
  padding: 18px 50px;
  background: #000;
  color: #fff;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 0.9rem;
  letter-spacing: 2px;
  transition: all 0.3s;
  border: 1px solid #000;
  align-self: flex-start;

  &:hover {
    background: #fff;
    color: #000;
  }

  @media (max-width: 768px) {
    align-self: center; 
    width: 100%;
    text-align: center;
  }
`;

// --- NEWS GRID ---
const HomeNewsSection = styled.section`
  padding: 100px 5%;
  background: #fff;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const HomeNewsHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 60px;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const HomeSectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  text-transform: uppercase;
  margin: 0;
  letter-spacing: -1px;
`;

const HomeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); 
  gap: 40px;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

// NA TO:
const HomeNewsCard = styled(Link)`
  text-decoration: none; /* Usuwa domyślne podkreślenie linków */
  background: #fff;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  
  &:hover img {
    transform: scale(1.05);
  }
`;
const HomeNewsImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  background: #f0f0f0;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    height: 220px;
  }
`;

const HomeNewsImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1);
`;

const HomeNewsDate = styled.span`
  font-size: 0.8rem;
  color: #999;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 10px;
  display: block;
`;

const HomeNewsTitle = styled.h4`
  font-size: 1.6rem;
  margin: 0 0 10px 0;
  color: #000;
  line-height: 1.1;
  font-weight: 800;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HomeNewsExcerpt = styled.p`
  font-size: 1rem;
  color: #555;
  line-height: 1.5;
  margin: 0;
`;

// --- SPONSORS CAROUSEL ---
const HomeSponsorsSection = styled.section`
  padding: 60px 0;
  background: #fff;
  border-top: 1px solid #eee;
  overflow: hidden;
  position: relative;
`;

const HomeMarqueeWrapper = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 150px;
    height: 100%;
    z-index: 2;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, #fff, transparent);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, #fff, transparent);
  }
  
  @media (max-width: 768px) {
    &::before, &::after {
      width: 50px;
    }
  }
`;

const HomeMarqueeTrack = styled.div`
  display: flex;
  align-items: center;
  animation: ${HomeMarqueeAnim} 30s linear infinite;
  white-space: nowrap;
  
  &:hover {
    animation-play-state: paused;
  }
`;

const HomeSponsorItem = styled.div`
  margin: 0 40px;
  opacity: 0.5;
  transition: opacity 0.3s;
  
  &:hover {
    opacity: 1;
  }
  
  span {
    font-size: 1.5rem;
    font-weight: 900;
    color: #000;
    text-transform: uppercase;
    border: 2px solid #000;
    padding: 10px 20px;
    display: inline-block;
  }

  @media (max-width: 768px) {
    margin: 0 20px;
    span {
      font-size: 1.1rem;
      padding: 8px 15px;
    }
  }
`;


// --- GŁÓWNY KOMPONENT ---

export const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [sponsorsData, setSponsorsData] = useState([]); // <--- DODANE
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [matches, setMatches] = useState([]); // NOWY STAN DLA MECZÓW
  // Stan kontrolujący strzałki karuzeli
  const [showArrows, setShowArrows] = useState(false);
  const eventsScrollRef = useRef(null);
  const adres = "https://api.pth-hokej.pl";

  useEffect(() => {
    console.log(sponsorsData)
  }, [sponsorsData])
  useEffect(() => {
    const fetchDataAndPreload = async () => {
      try {
        // POBIERANIE DANYCH RÓWNOLEGLE (dodano pobieranie meczów)
        const [eventsRes, newsRes, galleryRes, sponsorsRes] = await Promise.all([
          axios.get(`${adres}/api/scheduledevents`),
          axios.get(`${adres}/api/aktualnosci`),
          axios.get(`${adres}/api/gallery/slider`),
          axios.get(`${adres}/api/sponsors`),

        ]);

        setEvents(eventsRes.data.sort((a, b) => new Date(a.data) - new Date(b.data)));
        setNews(newsRes.data.slice(0, 3));
        setSponsorsData(sponsorsRes.data); // <--- DODANE

        const fetchedGallery = galleryRes.data;
        setGallery(fetchedGallery);

        const imagesToPreload = fetchedGallery.length > 0
          ? fetchedGallery.map(item => item.link)
          : defaultSlides.map(slide => slide.link);

        const imagePromises = imagesToPreload.map(url => {
          return new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = resolve;
            img.onerror = resolve;
          });
        });

        await Promise.all(imagePromises);
      } catch (error) {
        console.error("Błąd API podczas pobierania danych:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataAndPreload();
  }, []);
  // Logika sprawdzania, czy wydarzenia wychodzą poza ekran
  useEffect(() => {
    const checkOverflow = () => {
      if (eventsScrollRef.current) {
        const { scrollWidth, clientWidth } = eventsScrollRef.current;
        // Pokazuje strzałki tylko wtedy, gdy szerokość zawartości jest większa niż szerokość kontenera
        setShowArrows(scrollWidth > clientWidth);
      }
    };

    // Sprawdzamy po załadowaniu wydarzeń (timeout by dać czas na render)
    if (events.length > 0) {
      setTimeout(checkOverflow, 100);
    }

    // Sprawdzamy przy zmianie rozmiaru okna
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [events]);


  useEffect(() => {
    if (isLoading) return;
    const slideTimer = setInterval(() => {
      const length = gallery.length > 0 ? gallery.length : defaultSlides.length;
      setCurrentSlide(prev => (prev + 1) % length);
    }, 5000);
    return () => clearInterval(slideTimer);
  }, [gallery, isLoading]);

  const scrollEvents = (direction) => {
    if (eventsScrollRef.current) {
      const { current } = eventsScrollRef;
      // Przewijamy o szerokość jednej karty + gap
      const scrollAmount = current.firstChild ? current.firstChild.clientWidth + 20 : 340;
      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const activeSlides = gallery.length > 0
    ? gallery.map((item) => ({
      link: item.link,
      title: "PTH Koziołki", // Możesz też tu ustawić inny napis główny
      desc: item.description || "Duma Poznania. Walczymy do końca." // Pobiera nowy opis z bazy!
    }))
    : defaultSlides;
  const displaySponsors = [...sponsorsData, ...sponsorsData];
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return { day: "--", month: "---" };
    const day = d.getDate();
    const month = d.toLocaleString('pl-PL', { month: 'short' }).toUpperCase().replace('.', '');
    return { day, month };
  };
  // POMOCNICZA FUNKCJA DO FORMATOWANIA DATY MECZU ("24 PAŹDZIERNIKA")
  const formatMatchDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = d.getDate();
    const month = d.toLocaleString('pl-PL', { month: 'long' }).toUpperCase();
    return `${day} ${month}`;
  };
  if (isLoading) {
    return (
      <HomeLoader>
        <img src={"./logo_PTH.png"} alt="Logo PTH Koziołki" />
        <span>Ładowanie...</span>
      </HomeLoader>
    );
  }

  return (
    <>
      <HomeGlobalStyle />
      <HomeContainer>

        {/* HERO SLIDER */}
        <HomeHeroSlider>
          {activeSlides.map((slide, index) => (
            <HomeSlide key={index} active={index === currentSlide}>
              <HomeSlideImage src={slide.link} alt="Slide" active={index === currentSlide} />

              <HomeSlideContent>
                {/* To już miałeś (Opis): */}
                <HomeSlideDesc active={index === currentSlide}>{slide.desc}</HomeSlideDesc>
              </HomeSlideContent>

            </HomeSlide>
          ))}
          <HomeSliderControls>
            {activeSlides.map((_, index) => (
              <HomeDot
                key={index}
                active={index === currentSlide}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </HomeSliderControls>
        </HomeHeroSlider>

        {/* EVENTS CAROUSEL */}
        {events.length > 0 && (
          <HomeEventsSection>
            <HomeEventsHeader>
              <HomeSectionLabel>Nadchodzące Wydarzenia</HomeSectionLabel>
            </HomeEventsHeader>

            <HomeEventsRelativeWrapper>
              <HomeNavBtn
                direction="left"
                onClick={() => scrollEvents('left')}
                show={showArrows}
              >
                <ChevronLeft />
              </HomeNavBtn>

              <HomeEventsWrapper ref={eventsScrollRef}>
                {events.map((evt, idx) => {
                  const { day, month } = formatDate(evt.data);
                  const eventImage = evt.zdjecie || eventFallbacks[idx % eventFallbacks.length];

                  return (
                    <HomeEventCard key={evt._id || idx}>
                      <HomeEventImageWrapper>
                        <HomeEventImg src={eventImage} alt={evt.nazwa} />
                      </HomeEventImageWrapper>

                      <HomeEventContent className="event-content">
                        <HomeEventDate>
                          <span className="day">{day}</span>
                          <span className="month">{month}</span>
                        </HomeEventDate>
                        <HomeEventInfo>
                          <HomeEventTitle>{evt.nazwa}</HomeEventTitle>
                          <HomeEventMeta>{evt.adres}</HomeEventMeta>
                        </HomeEventInfo>
                      </HomeEventContent>
                    </HomeEventCard>
                  );
                })}
              </HomeEventsWrapper>

              <HomeNavBtn
                direction="right"
                onClick={() => scrollEvents('right')}
                show={showArrows}
              >
                <ChevronRight />
              </HomeNavBtn>
            </HomeEventsRelativeWrapper>
          </HomeEventsSection>
        )}

        {/* ABOUT CLUB SECTION */}
        <HomeAboutSection>
          <HomeAboutContent>
            <EditableText textId="home_about_title" defaultText="PTH Koziołki">
              <HomeAboutTitle />
            </EditableText>

            <EditableText textId="home_about_desc" defaultText="Jesteśmy dumą Poznania...">
              <HomeAboutText />
            </EditableText>

            <HomeAboutStats>
              <HomeStatItem>
                <EditableText textId="home_stat_year" defaultText="2005">
                  <strong></strong>
                </EditableText>
                <EditableText textId="home_stat_year_desc" defaultText="Rok założenia">
                  <span></span>
                </EditableText>
              </HomeStatItem>

              <HomeStatItem>
                <EditableText textId="home_stat_players" defaultText="150+">
                  <strong></strong>
                </EditableText>
                <EditableText textId="home_stat_players_desc" defaultText="Młodych Wilków">
                  <span></span>
                </EditableText>
              </HomeStatItem>

              <HomeStatItem>
                <EditableText textId="home_stat_championships" defaultText="5">
                  <strong></strong>
                </EditableText>
                <EditableText textId="home_stat_championships_desc" defaultText="Mistrzostw Polski">
                  <span></span>
                </EditableText>
              </HomeStatItem>

            </HomeAboutStats>

            <HomeCTAButton to="/kim-jestesmy">
              Historia Klubu
            </HomeCTAButton>
          </HomeAboutContent>

          <HomeValuesGrid>
            <HomeValueCard>
              <HomeIconWrapper>
                <Heart />
              </HomeIconWrapper>
              <EditableText textId="home_value_passion" defaultText="Pasja">
                <h4></h4>
              </EditableText>
              <EditableText textId="home_value_passion_desc" defaultText="Serce zostawiamy na lodzie. Każdy trening to krok do mistrzostwa.">
                <p></p>
              </EditableText>
            </HomeValueCard>

            <HomeValueCard>
              <HomeIconWrapper>
                <Clock />
              </HomeIconWrapper>
              <EditableText textId="home_value_discipline" defaultText="Dyscyplina">
                <h4></h4>
              </EditableText>
              <EditableText textId="home_value_discipline_desc" defaultText="Punktualność, systematyczność i szacunek do zasad to podstawa sukcesu.">
                <p></p>
              </EditableText>
            </HomeValueCard>

            <HomeValueCard>
              <HomeIconWrapper>
                <Users />
              </HomeIconWrapper>
              <EditableText textId="home_value_team" defaultText="Zespół">
                <h4></h4>
              </EditableText>
              <EditableText textId="home_value_team_desc" defaultText="Jeden za wszystkich, wszyscy za jednego. Wataha zawsze trzyma się razem.">
                <p></p>
              </EditableText>
            </HomeValueCard>

            <HomeValueCard>
              <HomeIconWrapper>
                <Handshake />
              </HomeIconWrapper>
              <EditableText textId="home_value_fairplay" defaultText="Fair Play">
                <h4></h4>
              </EditableText>
              <EditableText textId="home_value_fairplay_desc" defaultText="Wygrywamy dzięki umiejętnościom, a nie faulom. Szacunek dla rywala.">
                <p></p>
              </EditableText>
            </HomeValueCard>
          </HomeValuesGrid>
        </HomeAboutSection>

        {/* DYNAMICZNA SEKCJA NEXT MATCH */}
        {/* <HomeMatchSection>
          <HomeSectionLabel style={{ alignSelf: 'flex-start' }}>Nadchodzące Mecze</HomeSectionLabel>

          {matches.length > 0 ? (
            matches.map((match) => {

              // Wyciąganie danych z dołączonego rocznika
              const rocznikData = match.rocznikData || {};
              const isMongoId = match.rocznik && match.rocznik.length === 24;
              const displayRocznik = rocznikData.rocznik || (isMongoId ? "" : match.rocznik);

              const trener = rocznikData.trener || "";
              const color1 = rocznikData.kolor1 || null;
              const color2 = rocznikData.kolor2 || null;

              return (
                <HomeVersusCard key={match._id} style={{ marginBottom: matches.length > 1 ? '40px' : '0' }}>

                  <HomeTeamCol align="right">
                    <HomeTeamName align="right" color1={color1} color2={color2}>
                      Wilki {displayRocznik}
                    </HomeTeamName>
                    {trener && (
                      <HomeCoachLabel align="right">
                        Trener: <span>{trener}</span>
                      </HomeCoachLabel>
                    )}
                  </HomeTeamCol>

                  <HomeMatchInfo>
                    <HomeMatchVs>VS</HomeMatchVs>
                    <HomeMatchDate>{formatMatchDate(match.date)} | {match.time}</HomeMatchDate>
                    <HomeMatchLocation>{match.address}</HomeMatchLocation>

                  </HomeMatchInfo>

                  <HomeTeamCol align="left">
                    <HomeTeamName align="left" color="#555">
                      {match.opponentName}
                    </HomeTeamName>
                  </HomeTeamCol>

                </HomeVersusCard>
              );
            })
          ) : (
            <div style={{ padding: '80px 0', color: '#999', fontSize: '1.2rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
              Brak nadchodzących meczów
            </div>
          )}
        </HomeMatchSection> */}

        {/* NEWS GRID */}
        <HomeNewsSection>
          <HomeNewsHeaderRow>
            <HomeSectionTitle>Aktualności</HomeSectionTitle>
            <Link to="/aktualnosci" style={{ color: '#000', textDecoration: 'underline', fontWeight: 'bold' }}>Wszystkie wpisy</Link>
          </HomeNewsHeaderRow>

          <HomeGrid>
            {news.length > 0 ? news.map((item) => (

              /* TUTAJ ZMIANA: Dodano właściwość "to" z hashem (#) i ID artykułu */
              <HomeNewsCard key={item._id} to={`/aktualnosci#${item._id}`}>

                <HomeNewsImageWrapper>
                  <HomeNewsImage src={item.zdjecie || "https://images.unsplash.com/photo-1547054705-c665267da38e?auto=format&fit=crop&q=80&w=800"} alt={item.tytul} />
                </HomeNewsImageWrapper>
                <HomeNewsDate>{item.data}</HomeNewsDate>
                <HomeNewsTitle>{item.tytul}</HomeNewsTitle>
                <HomeNewsExcerpt>{item.tresc.substring(0, 100)}...</HomeNewsExcerpt>

              </HomeNewsCard>

            )) : (
              <p style={{ color: '#999' }}>Brak aktualności.</p>
            )}
          </HomeGrid>
        </HomeNewsSection>

        {/* SPONSORS CAROUSEL */}

        <HomeSponsorsSection>
          <HomeMarqueeWrapper>
            <HomeMarqueeTrack>
              {displaySponsors.map((sponsor, index) => (
                <HomeSponsorItem key={`index${sponsor.nazwa}`}>
                  <span>{sponsor?.nazwa || ""}</span>
                </HomeSponsorItem>
              ))}
            </HomeMarqueeTrack>
          </HomeMarqueeWrapper>
        </HomeSponsorsSection>
      </HomeContainer>
    </>
  );
};

export default HomePage;